<template>
  <div class="volume-pricing-rules">
    <EmptyState
      v-if="!localRules.length"
      title="Nog geen aantalregels"
      :description="emptyDescription"
    >
      <Btn variant="secondary" icon="plus" @click="addRule">Aantalregel toevoegen</Btn>
    </EmptyState>

    <div v-else class="volume-pricing-rules__list">
      <div v-for="(rule, index) in localRules" :key="rule.id" class="volume-rule">
        <div class="volume-rule__header">
          <div class="volume-rule__title">
            <InplaceEdit
              :id="`volume-rule-label-${index}`"
              v-model="rule.label"
              placeholder="Naam van regel..."
              class="volume-rule__label"
            />
            <span class="volume-rule__scope-chip">{{ getScopeLabel(rule.scope) }}</span>
          </div>

          <div class="volume-rule__actions">
            <ToggleInput :id="`volume-rule-enabled-${index}`" label="Actief" v-model="rule.enabled" />
            <Btn icon="trash" variant="outlined" size="lg" @click="removeRule(index)" />
          </div>
        </div>

        <div class="volume-rule__scope-row">
          <SelectInput
            v-if="allowedScopes.length > 1"
            :id="`volume-rule-scope-${index}`"
            v-model="rule.scope"
            :options="scopeOptions"
            label="Scope"
            class="volume-rule__scope"
          />
          <span v-else class="volume-rule__locked-scope">{{ getScopeLabel(rule.scope) }}</span>

          <SelectInput
            v-if="rule.scope === 'product_group'"
            :id="`volume-rule-group-${index}`"
            v-model="rule.groupId"
            :options="groupOptions"
            label="Productgroep"
            placeholder="Kies productgroep..."
          />

          <SelectInput
            v-if="rule.scope === 'same_product' && basisOptions.length > 1"
            :id="`volume-rule-basis-${index}`"
            v-model="rule.basis.fieldId"
            :options="basisOptions"
            label="Staffelbasis"
          />
        </div>

        <div class="volume-rule__tiers">
          <div
            v-for="(tier, tierIndex) in rule.tiers"
            :key="tier.id"
            class="volume-tier"
          >
            <div class="volume-tier__range">
              <NumberInput
                :id="`volume-rule-min-${index}-${tierIndex}`"
                v-model="tier.minQuantity"
                :label="getTierRangeLabel(rule, 'Vanaf')"
                :min="isFieldBasis(rule) ? 0 : 1"
                :step="isFieldBasis(rule) ? 0.01 : 1"
              />
              <NumberInput
                :id="`volume-rule-max-${index}-${tierIndex}`"
                v-model="tier.maxQuantity"
                :label="getTierRangeLabel(rule, 'Tot en met')"
                :min="isFieldBasis(rule) ? 0 : 1"
                :step="isFieldBasis(rule) ? 0.01 : 1"
                placeholder="Geen maximum"
              />
            </div>

            <div class="volume-tier__effect">
              <SelectInput
                :id="`volume-rule-action-${index}-${tierIndex}`"
                v-model="tier.action.type"
                :options="actionTypeOptions"
                label="Actie"
              />

              <NumberInput
                v-if="tier.action.type !== 'formula'"
                :id="`volume-rule-value-${index}-${tierIndex}`"
                v-model="tier.action.value"
                :label="getActionValueLabel(tier.action.type)"
                :min="getActionValueMin(tier.action.type)"
                :max="getActionValueMax(tier.action.type)"
                :step="getActionValueStep(tier.action.type)"
              />

              <TextInput
                v-else
                :id="`volume-rule-formula-${index}-${tierIndex}`"
                v-model="tier.action.expression"
                label="Formule"
                :placeholder="isFieldBasis(rule) ? '{same_product_metric} * -5' : '{total} * -0.05'"
              />
            </div>

            <div class="volume-tier__footer">
              <span>{{ formatTierPreview(rule, tier) }}</span>
              <Btn
                v-if="rule.tiers.length > 1"
                icon="minus"
                variant="plain"
                size="sm"
                @click="removeTier(index, tierIndex)"
              />
            </div>
          </div>

          <Btn variant="dashed" size="sm" icon="plus" @click="addTier(index)">
            Staffel toevoegen
          </Btn>
        </div>

        <div class="volume-rule__preview">
          <span class="volume-rule__preview-label">Preview</span>
          <span>{{ formatPreview(rule) }}</span>
        </div>

        <p class="volume-rule__scope-help">{{ getScopeHelp(rule.scope, rule) }}</p>
      </div>

      <Btn variant="dashed" icon="plus" class="volume-pricing-rules__add" @click="addRule">
        Aantalregel toevoegen
      </Btn>
    </div>
  </div>
</template>

<script>
  import { ref, computed, watch, nextTick } from 'vue';
  import { uid } from 'uid';

  import NumberInput from '@/components/inputs/NumberInput.vue';
  import SelectInput from '@/components/inputs/SelectInput.vue';
  import TextInput from '@/components/inputs/TextInput.vue';
  import ToggleInput from '@/components/inputs/ToggleInput.vue';
  import Btn from '@/components/partials/Btn.vue';
  import EmptyState from '@/components/partials/EmptyState.vue';
  import InplaceEdit from '@/components/partials/InplaceEdit.vue';

  export default {
    name: 'VolumePricingRulesEditor',

    components: {
      NumberInput,
      SelectInput,
      TextInput,
      ToggleInput,
      Btn,
      EmptyState,
      InplaceEdit,
    },

    props: {
      modelValue: {
        type: Array,
        default: () => [],
      },
      allowedScopes: {
        type: Array,
        default: () => ['same_product', 'cart_total'],
      },
      productName: {
        type: String,
        default: 'dit product',
      },
      groupOptions: {
        type: Array,
        default: () => [],
      },
      // Numerieke opties en berekende velden van het product waarop een rule
      // kan staffelen: [{ value, label, unit? }]. Leeg = alleen aantal-basis.
      basisFieldOptions: {
        type: Array,
        default: () => [],
      },
    },

    emits: ['update:modelValue'],

    setup(props, { emit }) {
      const normalizeRule = (rule = {}) => {
        const scope = props.allowedScopes.includes(rule.scope)
          ? rule.scope
          : props.allowedScopes[0] || 'same_product';

        // Metric-staffels (bijv. m²) mogen bij 0 beginnen; aantal-staffels
        // starten op minimaal 1.
        const hasFieldBasis = rule.basis?.type === 'field' && rule.basis?.fieldId;
        const toTierNumber = (value, fallback) => {
          const parsed = Number(value);
          return Number.isFinite(parsed) ? parsed : fallback;
        };
        const tierMinFloor = hasFieldBasis ? 0 : 1;

        const fallbackTier = {
          id: `${rule.id || 'volume_rule'}_tier_default`,
          minQuantity: Math.max(tierMinFloor, toTierNumber(rule.minQuantity, tierMinFloor)),
          maxQuantity: rule.maxQuantity ?? null,
          action: {
            type: rule.action?.type || rule.type || 'discount',
            value: Number(rule.action?.value ?? rule.value ?? 5),
            expression: rule.action?.expression || rule.expression || '',
          },
        };

        return {
          id: rule.id || `volume_rule_${uid()}`,
          label: rule.label || '',
          enabled: rule.enabled !== false,
          showInQuote: rule.showInQuote !== false,
          scope,
          groupId: rule.groupId || rule.categoryId || null,
          groupName: rule.groupName || rule.categoryName || '',
          basis: {
            fieldId:
              rule.basis?.type === 'field' && rule.basis?.fieldId ? String(rule.basis.fieldId) : '',
          },
          tiers: Array.isArray(rule.tiers) && rule.tiers.length
            ? rule.tiers.map((tier, index) => ({
                id: tier.id || `${rule.id || 'volume_rule'}_tier_${index}`,
                minQuantity: Math.max(tierMinFloor, toTierNumber(tier.minQuantity, tierMinFloor)),
                maxQuantity: tier.maxQuantity ?? null,
                action: {
                  type: tier.action?.type || tier.type || 'discount',
                  value: Number(tier.action?.value ?? tier.value ?? 5),
                  expression: tier.action?.expression || tier.expression || '',
                },
              }))
            : [fallbackTier],
        };
      };

      const localRules = ref(props.modelValue.map(normalizeRule));
      const isUpdatingFromExternal = ref(false);

      watch(
        () => props.modelValue,
        (rules) => {
          const nextRules = (rules || []).map(normalizeRule);
          if (JSON.stringify(nextRules) === JSON.stringify(localRules.value)) return;
          isUpdatingFromExternal.value = true;
          localRules.value = nextRules;
          nextTick(() => {
            isUpdatingFromExternal.value = false;
          });
        },
        { deep: true }
      );

      const buildBasisPayload = (rule) => {
        const fieldId = rule.scope === 'same_product' ? rule.basis?.fieldId : '';
        if (!fieldId) return { type: 'quantity' };

        const field = props.basisFieldOptions.find((option) => option.value === fieldId);
        return {
          type: 'field',
          fieldId,
          fieldLabel: field?.label || '',
          unit: field?.unit || '',
        };
      };

      watch(
        localRules,
        (rules) => {
          if (isUpdatingFromExternal.value) return;
          emit(
            'update:modelValue',
            rules.map((rule) => ({
              ...rule,
              groupName:
                rule.scope === 'product_group'
                  ? props.groupOptions.find((group) => group.value === rule.groupId)?.label || rule.groupName
                  : '',
              basis: buildBasisPayload(rule),
              tiers: rule.tiers.map((tier) => ({ ...tier, action: { ...tier.action } })),
            }))
          );
        },
        { deep: true }
      );

      const scopeOptions = computed(() =>
        props.allowedScopes.map((scope) => ({
          value: scope,
          label: getScopeLabel(scope),
        }))
      );

      const emptyDescription = computed(() => {
        if (props.allowedScopes.length === 1 && props.allowedScopes[0] === 'same_product') {
          return 'Maak regels voor aantallen van dit product, ook wanneer klanten meerdere configuraties toevoegen.';
        }
        if (props.allowedScopes.length === 1 && props.allowedScopes[0] === 'cart_total') {
          return 'Maak regels voor het totaal aantal producten in de offerte.';
        }
        return 'Maak regels voor aantallen op productniveau of over de hele offerte.';
      });

      const actionTypeOptions = [
        { value: 'discount', label: 'Korting percentage (%)' },
        { value: 'subtract', label: 'Bedrag aftrekken (EUR)' },
        { value: 'add', label: 'Bedrag toevoegen (EUR)' },
        { value: 'multiply', label: 'Vermenigvuldigen met factor' },
        { value: 'override', label: 'Prijs overschrijven (EUR)' },
        { value: 'formula', label: 'Aangepaste formule' },
      ];

      const basisOptions = computed(() => [
        { value: '', label: 'Aantal producten' },
        ...props.basisFieldOptions.map((field) => ({
          value: field.value,
          label: field.unit ? `${field.label} (${field.unit})` : field.label,
        })),
      ]);

      function isFieldBasis(rule) {
        return rule.scope === 'same_product' && !!rule.basis?.fieldId;
      }

      function getBasisField(rule) {
        if (!isFieldBasis(rule)) return null;
        return props.basisFieldOptions.find((option) => option.value === rule.basis.fieldId) || null;
      }

      function getTierRangeLabel(rule, prefix) {
        const field = getBasisField(rule);
        if (!field) return prefix;
        return field.unit ? `${prefix} (${field.unit})` : `${prefix} (${field.label})`;
      }

      function getScopeLabel(scope) {
        if (scope === 'product_group') return 'Productgroep';
        if (scope === 'cart_total') return 'Hele offerte';
        return 'Dit product';
      }

      function getScopeHelp(scope, rule = null) {
        if (scope === 'product_group') {
          return 'Telt alle producten in de gekozen categorie/productgroep bij elkaar op.';
        }
        if (scope === 'cart_total') {
          return 'Telt alle product quantities in de cart bij elkaar op.';
        }
        const field = rule ? getBasisField(rule) : null;
        if (field) {
          return `Telt "${field.label}" van alle configuraties van ${props.productName || 'dit product'} bij elkaar op (× aantal). In formules beschikbaar als {same_product_metric}.`;
        }
        return `Telt alle configuraties van ${props.productName || 'dit product'} bij elkaar op.`;
      }

      function getActionValueLabel(type) {
        const labels = {
          add: 'Bedrag (EUR)',
          subtract: 'Bedrag (EUR)',
          discount: 'Percentage (%)',
          multiply: 'Factor',
          override: 'Nieuwe prijs (EUR)',
        };
        return labels[type] || 'Waarde';
      }

      function getActionValueMin(type) {
        return type === 'multiply' ? 0.0001 : 0;
      }

      function getActionValueMax(type) {
        return type === 'discount' ? 100 : null;
      }

      function getActionValueStep(type) {
        if (type === 'multiply') return 0.0001;
        if (type === 'discount') return 0.1;
        return 0.01;
      }

      function formatTierRange(rule, tier) {
        const fieldBasis = isFieldBasis(rule);
        const min = Number(tier.minQuantity) || (fieldBasis ? 0 : 1);
        const max = tier.maxQuantity === null || tier.maxQuantity === '' ? null : Number(tier.maxQuantity);
        const unit = getBasisField(rule)?.unit || '';
        const suffix = unit ? ` ${unit}` : '';
        if (!max) return `${min}+${suffix}`;
        if (min === max) return `${min}${suffix}`;
        return `${min}-${max}${suffix}`;
      }

      function getScopePreviewLabel(rule) {
        if (rule.scope === 'product_group') {
          const group = props.groupOptions.find((option) => option.value === rule.groupId);
          return group?.label || rule.groupName || 'gekozen productgroep';
        }
        if (rule.scope === 'cart_total') return 'producten in de offerte';
        const field = getBasisField(rule);
        if (field) {
          return `${field.label} van ${props.productName || 'dit product'} (totaal)`;
        }
        return props.productName || 'dit product';
      }

      function formatTierPreview(rule, tier) {
        const range = formatTierRange(rule, tier);
        const scopeLabel = getScopePreviewLabel(rule);

        switch (tier.action.type) {
          case 'add':
            return `${range} ${scopeLabel}: EUR ${tier.action.value || 0} toevoegen.`;
          case 'subtract':
            return `${range} ${scopeLabel}: EUR ${tier.action.value || 0} aftrekken.`;
          case 'discount':
            return `${range} ${scopeLabel}: ${tier.action.value || 0}% korting.`;
          case 'multiply':
            return `${range} ${scopeLabel}: totaal vermenigvuldigen met ${tier.action.value || 1}.`;
          case 'override':
            return `${range} ${scopeLabel}: prijs overschrijven naar EUR ${tier.action.value || 0}.`;
          case 'formula':
            return `${range} ${scopeLabel}: formule ${tier.action.expression || '(leeg)'}.`;
          default:
            return `${range} ${scopeLabel}: regel toepassen.`;
        }
      }

      function formatPreview(rule) {
        if (!rule.tiers?.length) return 'Geen staffels';
        return rule.tiers.map((tier) => formatTierPreview(rule, tier)).join(' · ');
      }

      function addRule() {
        localRules.value.push(
          normalizeRule({
            label: props.allowedScopes[0] === 'cart_total' ? 'Offerte aantalkorting' : 'Product aantalkorting',
            scope: props.allowedScopes[0] || 'same_product',
            tiers: [
              {
                id: `volume_tier_${uid()}`,
                minQuantity: 1,
                maxQuantity: null,
                action: { type: 'discount', value: 5 },
              },
            ],
          })
        );
      }

      function addTier(ruleIndex) {
        const tiers = localRules.value[ruleIndex].tiers;
        const previous = tiers[tiers.length - 1];
        const previousMax = Number(previous?.maxQuantity) || Number(previous?.minQuantity) || 1;

        tiers.push({
          id: `volume_tier_${uid()}`,
          minQuantity: previousMax + 1,
          maxQuantity: null,
          action: { type: 'discount', value: 5 },
        });
      }

      function removeTier(ruleIndex, tierIndex) {
        localRules.value[ruleIndex].tiers.splice(tierIndex, 1);
      }

      function removeRule(index) {
        localRules.value.splice(index, 1);
      }

      return {
        localRules,
        scopeOptions,
        emptyDescription,
        actionTypeOptions,
        basisOptions,
        isFieldBasis,
        getTierRangeLabel,
        getScopeLabel,
        getScopeHelp,
        getActionValueLabel,
        getActionValueMin,
        getActionValueMax,
        getActionValueStep,
        formatTierPreview,
        formatPreview,
        addRule,
        addTier,
        removeTier,
        removeRule,
      };
    },
  };
</script>

<style scoped lang="scss">
  .volume-pricing-rules {
    &__list {
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }

    &__add {
      align-self: flex-start;
    }
  }

  .volume-rule {
    border: 1px solid var(--slate-200);
    border-radius: 0.75rem;
    background: var(--slate-50);
    padding: 1rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;

    &__header {
      display: grid;
      grid-template-columns: minmax(0, 1fr) auto;
      gap: 1rem;
      align-items: center;
    }

    &__title {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      min-width: 0;
    }

    &__label {
      min-width: 12rem;
    }

    &__scope-chip {
      border: 1px solid var(--brand-200);
      color: var(--brand-700);
      background: var(--brand-50);
      border-radius: 999px;
      padding: 0.2rem 0.55rem;
      font-size: 0.75rem;
      font-weight: 600;
      white-space: nowrap;
    }

    &__actions {
      display: flex;
      align-items: center;
      gap: 0.75rem;
    }

    &__scope-row {
      display: grid;
      grid-template-columns: minmax(14rem, 1fr) minmax(14rem, 1fr);
      gap: 0.75rem;
      align-items: end;
    }

    &__tiers {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
    }

    &__text,
    &__locked-scope {
      color: var(--slate-700);
      font-size: 0.875rem;
      padding-bottom: 0.75rem;
      white-space: nowrap;
    }

    &__locked-scope {
      font-weight: 600;
      color: var(--slate-900);
    }

    &__preview {
      display: flex;
      gap: 0.75rem;
      align-items: center;
      border: 1px solid var(--slate-200);
      border-radius: 0.5rem;
      background: #ffffff;
      padding: 0.65rem 0.75rem;
      color: var(--slate-700);
      font-size: 0.875rem;
    }

    &__preview-label {
      color: var(--slate-500);
      font-size: 0.75rem;
      font-weight: 700;
      letter-spacing: 0.04em;
      text-transform: uppercase;
    }

    &__scope-help {
      margin: -0.35rem 0 0;
      color: var(--slate-500);
      font-size: 0.8125rem;
    }

    @include lg-down {
      &__header,
      &__scope-row {
        grid-template-columns: 1fr;
      }

      &__actions,
      &__title {
        align-items: flex-start;
        flex-direction: column;
      }

      &__text,
      &__locked-scope {
        padding-bottom: 0;
      }
    }
  }

  .volume-tier {
    border: 1px solid var(--slate-200);
    border-radius: 0.625rem;
    background: #ffffff;
    padding: 0.75rem;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;

    &__range,
    &__effect {
      display: grid;
      grid-template-columns: minmax(8rem, 1fr) minmax(8rem, 1fr);
      gap: 0.75rem;
      align-items: end;
    }

    &__footer {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.75rem;
      color: var(--slate-600);
      font-size: 0.8125rem;
    }

    @include lg-down {
      &__range,
      &__effect,
      &__footer {
        grid-template-columns: 1fr;
      }
    }
  }
</style>
